import { useEffect, useMemo, useRef, useState } from "react";
import "./AmtrakSolari.css";

// Amtrak King Street Station-style Solari split-flap departure board.
//
// Visual reference: classic Solari Udine flipboards as seen in Philadelphia
// 30th Street Station and the older incarnation of King Street Station in
// Seattle. Warm cream characters on near-black tiles, with each character
// rendered as two stacked flaps split by a thin seam at mid-height. When a
// character changes the top flap rotates down through a few intermediate
// glyphs before settling on the new one, producing the iconic "clack-clack"
// ripple as the board refreshes.

type StopOption = {
  id: string;
  name: string;
  code: string;
  lat: number;
  lon: number;
  routes: string[];
};

type RouteGeometry = {
  route_id: string;
  route_short_name: string;
  route_long_name: string;
  route_color: string;
  stops: { id: string; name: string; code: string; lat: number; lon: number }[];
};

type Arrival = {
  route_id: string;
  route_short_name: string;
  route_color?: string;
  trip_id: string;
  trip_headsign: string;
  vehicle_id?: string;
  scheduled_arrival?: number;
  predicted_arrival?: number;
  scheduled_departure?: number;
  predicted_departure?: number;
  predicted: boolean;
  status?: string;
  stops_away: number;
};

type StopArrivals = {
  stop_id: string;
  stop_name: string;
  stop_code?: string;
  arrivals: Arrival[];
  generated: number;
};

const ARRIVAL_REFRESH_MS = 15_000;
const MAX_ROWS = 10;

// Column widths (number of character cells per column). Sized to match the
// widest plausible content for each field so the board has a consistent,
// rectilinear silhouette.
const COL_WIDTHS = {
  time: 5, // "12:34"
  dest: 22, // headsign, all caps
  train: 6, // "1L 042"
  stops: 9, // "● ● ● ● ◌"
  track: 5, // "TRK 1"
  status: 11, // "DELAYED 12m"
} as const;

export function AmtrakSolari({
  visible,
  historyTs = null,
}: {
  visible: boolean;
  historyTs?: number | null;
}) {
  const [stops, setStops] = useState<StopOption[]>([]);
  const [routes, setRoutes] = useState<RouteGeometry[]>([]);
  const [stopsErr, setStopsErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [data, setData] = useState<StopArrivals | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const historical = historyTs !== null;

  useEffect(() => {
    if (historical) {
      setNow(historyTs! * 1000);
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [historical, historyTs]);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/routes/geometry")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<RouteGeometry[]>;
      })
      .then((rs) => {
        if (cancelled) return;
        setRoutes(rs);
        const byId = new Map<string, StopOption>();
        for (const r of rs) {
          for (const s of r.stops ?? []) {
            const existing = byId.get(s.id);
            if (existing) {
              if (!existing.routes.includes(r.route_short_name)) {
                existing.routes.push(r.route_short_name);
              }
              continue;
            }
            byId.set(s.id, {
              id: s.id,
              name: s.name,
              code: s.code,
              lat: s.lat,
              lon: s.lon,
              routes: [r.route_short_name],
            });
          }
        }
        const list = Array.from(byId.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setStops(list);
        if (list.length > 0 && !selectedId) {
          // Default to a major station with lots of through-traffic so the
          // board renders something interesting on first paint. Westlake is
          // the best Link analogue to "King Street": downtown, both lines.
          const preferred =
            list.find((s) => /westlake/i.test(s.name)) ??
            list.find((s) => /international|chinatown|king/i.test(s.name)) ??
            list.find((s) => /sea-tac|seatac|airport/i.test(s.name)) ??
            list[0];
          setSelectedId(preferred.id);
        }
      })
      .catch((err) => {
        if (!cancelled) setStopsErr(String(err));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId || !visible) return;
    let cancelled = false;
    let timer: number | null = null;
    let debounce: number | null = null;

    const url = historical
      ? `/api/realtime/arrivals/at?stop_id=${encodeURIComponent(selectedId)}&ts=${encodeURIComponent(String(historyTs))}`
      : `/api/realtime/arrivals?stop_id=${encodeURIComponent(selectedId)}`;

    const load = () => {
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<StopArrivals>;
        })
        .then((d) => {
          if (cancelled) return;
          setData(d);
          setLoadErr(null);
        })
        .catch((err) => {
          if (!cancelled) setLoadErr(String(err));
        });
    };

    if (historical) {
      debounce = window.setTimeout(load, 120);
    } else {
      load();
      timer = window.setInterval(load, ARRIVAL_REFRESH_MS);
    }
    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
      if (debounce !== null) window.clearTimeout(debounce);
    };
  }, [selectedId, visible, historical, historyTs]);

  const selectedStop = useMemo(
    () => stops.find((s) => s.id === selectedId) ?? null,
    [stops, selectedId],
  );

  const rows = useMemo(
    () => buildRows(data, now, routes, selectedId),
    [data, now, routes, selectedId],
  );

  const stationLine = stationHeader(selectedStop);
  const updatedAt = data?.generated ? data.generated * 1000 : now;

  return (
    <div className="solari-wrap">
      <div className="solari-controls">
        <label className="solari-station-label" htmlFor="solari-station">
          Station
        </label>
        <select
          id="solari-station"
          className="solari-station-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {stops.length === 0 && <option value="">Loading stations…</option>}
          {stops.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.routes.length > 0 ? `  ·  ${s.routes.join(" / ")} Line` : ""}
            </option>
          ))}
        </select>
        {stopsErr && <span className="solari-error">{stopsErr}</span>}
      </div>

      <div
        className="solari-board"
        role="region"
        aria-label="Amtrak-style Solari departure board"
      >
        <div className="solari-brand-bar">
          <span className="solari-brand-stripe" />
          <span className="solari-brand-mark">AMTRAK</span>
          <span className="solari-brand-spacer" />
          <span className="solari-brand-departures">DEPARTURES</span>
        </div>

        <div className="solari-station-header">{stationLine}</div>

        <div className="solari-grid solari-grid-head" aria-hidden="true">
          <div className="solari-col solari-col-time">TIME</div>
          <div className="solari-col solari-col-dest">DESTINATION</div>
          <div className="solari-col solari-col-train">TRAIN</div>
          <div className="solari-col solari-col-stops">STOPS</div>
          <div className="solari-col solari-col-track">TRACK</div>
          <div className="solari-col solari-col-status">STATUS</div>
        </div>

        <div className="solari-rows">
          {Array.from({ length: MAX_ROWS }).map((_, i) => {
            const row = rows[i];
            const ripple = i * 80; // ms cascade between rows
            return (
              <div className="solari-grid solari-row" key={i}>
                <div className="solari-col solari-col-time">
                  <Flap
                    text={row?.time ?? ""}
                    width={COL_WIDTHS.time}
                    rowDelay={ripple}
                  />
                </div>
                <div className="solari-col solari-col-dest">
                  <Flap
                    text={row?.destination ?? ""}
                    width={COL_WIDTHS.dest}
                    rowDelay={ripple}
                    align="left"
                  />
                </div>
                <div className="solari-col solari-col-train">
                  <Flap
                    text={row?.train ?? ""}
                    width={COL_WIDTHS.train}
                    rowDelay={ripple}
                  />
                </div>
                <div className="solari-col solari-col-stops">
                  <Flap
                    text={row?.stops ?? ""}
                    width={COL_WIDTHS.stops}
                    rowDelay={ripple}
                  />
                </div>
                <div className="solari-col solari-col-track">
                  <Flap
                    text={row?.track ?? ""}
                    width={COL_WIDTHS.track}
                    rowDelay={ripple}
                  />
                </div>
                <div
                  className={`solari-col solari-col-status status-${row?.statusKind ?? "blank"}`}
                >
                  <Flap
                    text={row?.status ?? ""}
                    width={COL_WIDTHS.status}
                    rowDelay={ripple}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="solari-footer">
          {loadErr ? (
            <span className="solari-footer-err">SIGNAL LOST · {loadErr}</span>
          ) : (
            <span>
              DEPARTURES SHOWN PACIFIC TIME · UPDATED {formatHMS(updatedAt)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// One column of split-flap character cells. The component is "uncontrolled"
// in the sense that React only re-renders when `text` changes; the per-cell
// flip animation is driven by toggling a CSS class on the cell DOM node, so
// we don't churn a render tree of hundreds of <span>s sixty times a second.
function Flap({
  text,
  width,
  rowDelay = 0,
  align = "center",
}: {
  text: string;
  width: number;
  rowDelay?: number;
  align?: "center" | "left";
}) {
  const padded = padTo(text.toUpperCase(), width, align);
  return (
    <span className={`flap-row align-${align}`}>
      {Array.from({ length: width }).map((_, i) => (
        <FlapCell key={i} ch={padded[i]} delay={rowDelay + i * 35} />
      ))}
    </span>
  );
}

// A single split-flap character cell. When the target character changes we:
//   1) snapshot the previous char on the bottom half (the "old" plate)
//   2) flip the top half through a few random glyphs (rendered by mutating
//      a span's textContent imperatively to avoid React reconciliation)
//   3) settle on the new char
// All timing lives in CSS via `animation: solari-flip`. We just toggle the
// `.flipping` class and let it run.
function FlapCell({ ch, delay }: { ch: string; delay: number }) {
  const [shown, setShown] = useState(ch);
  const [prev, setPrev] = useState(ch);
  const [flipping, setFlipping] = useState(false);
  const topFlapRef = useRef<HTMLSpanElement | null>(null);
  const intermediateTimerRef = useRef<number | null>(null);
  const finishTimerRef = useRef<number | null>(null);
  const startTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (ch === shown) return;
    const target = ch;
    const wasShown = shown;

    // Pin the previous character on the bottom half of the cell so the
    // top flap appears to physically peel away from it.
    setPrev(wasShown);

    // Delay the flip start so the row ripples left-to-right and the board
    // ripples top-to-bottom, mimicking the cascade of solenoids firing in
    // sequence on a real Solari board.
    if (startTimerRef.current !== null) {
      window.clearTimeout(startTimerRef.current);
    }
    startTimerRef.current = window.setTimeout(() => {
      setFlipping(true);

      // Cycle through 2-3 intermediate glyphs by directly mutating the
      // top flap's text. Skipping React here keeps the animation buttery
      // even on a busy page.
      const intermediates = pickIntermediates(target, 3);
      let idx = 0;
      const tick = () => {
        if (topFlapRef.current) {
          topFlapRef.current.textContent = intermediates[idx] ?? target;
        }
        idx++;
        if (idx < intermediates.length) {
          intermediateTimerRef.current = window.setTimeout(tick, 90);
        }
      };
      tick();

      finishTimerRef.current = window.setTimeout(() => {
        setShown(target);
        setFlipping(false);
        if (topFlapRef.current) {
          topFlapRef.current.textContent = target;
        }
      }, 420);
    }, delay);

    return () => {
      if (startTimerRef.current !== null) {
        window.clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
      if (intermediateTimerRef.current !== null) {
        window.clearTimeout(intermediateTimerRef.current);
        intermediateTimerRef.current = null;
      }
      if (finishTimerRef.current !== null) {
        window.clearTimeout(finishTimerRef.current);
        finishTimerRef.current = null;
      }
    };
    // shown is intentionally excluded — we only want this to run when the
    // *target* character changes, not when we settle into it ourselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch, delay]);

  const blank = shown === " " && !flipping;

  return (
    <span
      className={`flap-cell${flipping ? " flipping" : ""}${blank ? " blank" : ""}`}
    >
      {/* Static halves (visible when not flipping). */}
      <span className="flap-half flap-top-static">{shown}</span>
      <span className="flap-half flap-bottom-static">{shown}</span>
      {/* The peeling top flap shows the OLD character, rotating away. */}
      <span className="flap-half flap-top-anim">{prev}</span>
      {/* The bottom half of the new character is revealed underneath. */}
      <span className="flap-half flap-bottom-anim" ref={topFlapRef}>
        {ch}
      </span>
      {/* The seam line down the middle of the cell. */}
      <span className="flap-seam" aria-hidden="true" />
    </span>
  );
}

const FLIP_GLYPHS = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789";

function pickIntermediates(target: string, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    let c = FLIP_GLYPHS[Math.floor(Math.random() * FLIP_GLYPHS.length)];
    if (c === target) {
      c = FLIP_GLYPHS[(FLIP_GLYPHS.indexOf(c) + 1) % FLIP_GLYPHS.length];
    }
    out.push(c);
  }
  return out;
}

function padTo(s: string, width: number, align: "center" | "left"): string {
  if (s.length >= width) return s.slice(0, width);
  const pad = width - s.length;
  if (align === "left") return s + " ".repeat(pad);
  const left = Math.floor(pad / 2);
  const right = pad - left;
  return " ".repeat(left) + s + " ".repeat(right);
}

type SolariRow = {
  key: string;
  time: string;
  destination: string;
  train: string;
  stops: string;
  track: string;
  status: string;
  statusKind: "ontime" | "delayed" | "boarding" | "departed" | "express";
  whenMs: number;
};

function buildRows(
  data: StopArrivals | null,
  nowMs: number,
  routes: RouteGeometry[],
  selectedStopId: string,
): SolariRow[] {
  if (!data) return [];
  const rows: SolariRow[] = [];
  for (const a of data.arrivals) {
    const schedSec = a.scheduled_departure || a.scheduled_arrival || 0;
    const predSec = a.predicted_departure || a.predicted_arrival || 0;
    const bestSec = predSec || schedSec;
    if (!bestSec) continue;
    const bestMs = bestSec * 1000;
    // Drop trips that have left more than 2 minutes ago, but keep recent
    // departures briefly so users can see "DEPARTED" clack onto the board.
    if (bestMs < nowMs - 120_000) continue;

    const etaMs = bestMs - nowMs;
    const delaySec = predSec && schedSec ? predSec - schedSec : 0;
    const cancelled = /cancel/i.test(a.status || "");

    let statusText: string;
    let statusKind: SolariRow["statusKind"];
    if (cancelled) {
      statusText = "CANCELLED";
      statusKind = "delayed";
    } else if (etaMs < 0) {
      statusText = "DEPARTED";
      statusKind = "departed";
    } else if (etaMs < 60_000) {
      statusText = "BOARDING";
      statusKind = "boarding";
    } else if (delaySec > 60) {
      const mins = Math.round(delaySec / 60);
      statusText = `DELAYED ${mins}m`;
      statusKind = "delayed";
    } else {
      statusText = "ON TIME";
      statusKind = "ontime";
    }

    rows.push({
      key: `${a.trip_id}-${schedSec}`,
      time: formatHM(bestMs),
      destination: cleanHeadsign(a.trip_headsign),
      train: trainNumber(a.route_short_name, a.vehicle_id),
      stops: stopsGlyph(a.stops_away),
      track: trackFor(a, routes, selectedStopId),
      status: statusText,
      statusKind,
      whenMs: bestMs,
    });
  }
  rows.sort((a, b) => a.whenMs - b.whenMs);
  return rows.slice(0, MAX_ROWS);
}

function cleanHeadsign(h: string): string {
  if (!h) return "—";
  return h.toUpperCase().replace(/\s+/g, " ").trim();
}

// Synthesize an Amtrak-ish train number from the route + last 3 of the
// vehicle id. Real Amtrak trains have three-digit numbers and a route
// prefix. Link doesn't, but "1L 042" reads as "1 Line, car 042".
function trainNumber(routeShort: string, vehicleId?: string): string {
  const m = (routeShort || "").match(/(\d+)/);
  const prefix = m ? `${m[1]}L` : "LK";
  if (!vehicleId) return `${prefix} —`;
  const digits = vehicleId.replace(/\D/g, "");
  const tail = digits ? digits.slice(-3).padStart(3, "0") : vehicleId.slice(-3).toUpperCase();
  return `${prefix} ${tail}`;
}

// Render stops_away as a row of dots: filled circles for stops behind us,
// an open circle for "this stop is next". For boarding trains (0 stops)
// show all five dots as filled.
function stopsGlyph(stopsAway: number): string {
  if (stopsAway < 0) return "—";
  const total = 5;
  if (stopsAway === 0) return "● ● ● ● ●";
  if (stopsAway >= total) return `${stopsAway} STOPS`;
  const filled = Math.max(0, total - stopsAway);
  let out = "";
  for (let i = 0; i < total; i++) {
    if (i > 0) out += " ";
    if (i < filled) out += "●";
    else if (i === filled) out += "◌";
    else out += "○";
  }
  return out;
}

// Sound Transit doesn't publish per-trip track assignments, so we synthesize
// one from route + direction using the Amtrak King Street convention of
// numbered tracks. Direction is inferred by checking whether the trip's
// destination comes before or after the current stop in the route's stop
// list.
function trackFor(
  a: Arrival,
  routes: RouteGeometry[],
  selectedStopId: string,
): string {
  const route = routes.find(
    (r) => r.route_short_name.toLowerCase() === (a.route_short_name || "").toLowerCase(),
  );
  let northbound = true;
  if (route && route.stops?.length) {
    const fromIdx = route.stops.findIndex((s) => s.id === selectedStopId);
    const destNorm = normStation(a.trip_headsign || "");
    let destIdx = -1;
    for (let i = 0; i < route.stops.length; i++) {
      const n = normStation(route.stops[i].name);
      if (n === destNorm || (destNorm && (n.includes(destNorm) || destNorm.includes(n)))) {
        destIdx = i;
        break;
      }
    }
    if (fromIdx >= 0 && destIdx >= 0) {
      // Stops are typically listed south-to-north for Link; later index =
      // further north.
      northbound = destIdx > fromIdx;
    }
  }
  const m = (a.route_short_name || "").match(/(\d+)/);
  const lineNum = m ? Number(m[1]) : 1;
  // 1 Line: tracks 1 (NB) / 2 (SB). 2 Line: tracks 3 (NB) / 4 (SB).
  let track: number;
  if (lineNum === 1) track = northbound ? 1 : 2;
  else if (lineNum === 2) track = northbound ? 3 : 4;
  else track = northbound ? 5 : 6;
  return `TRK ${track}`;
}

function normStation(name: string): string {
  return name
    .toLowerCase()
    .replace(/\bstation\b/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

function stationHeader(stop: StopOption | null): string {
  if (!stop) return "KING STREET STATION · SEATTLE WA";
  const name = stop.name.toUpperCase().replace(/\s+STATION$/i, "");
  return `${name} STATION · SEATTLE WA`;
}

function formatHM(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function formatHMS(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export default AmtrakSolari;
